/**
 * JWT READING. **THIS FILE VERIFIES NOTHING. IT IS NOT AN AUTHORIZATION MECHANISM.**
 * ================================================================================
 *
 * {@link decodeJwtClaims} splits a JWT on `.` and base64url-decodes the middle segment. It does not
 * check the signature, the issuer, the audience or the expiry. A JWT this function decodes happily may
 * have been written by anyone.
 *
 * WHY THAT IS ACCEPTABLE HERE, AND WHERE THE LINE IS:
 *
 *  · **The API is the authority.** Every request carries the access token to the API Gateway
 *    authorizer, which validates the signature against `jwks_uri` and derives the supplier scope from
 *    verified claims (`infra/`). The client cannot grant itself anything by believing a token, because
 *    the client is not the thing making the decision. A client-side check would be defence in depth at
 *    best and a false sense of security at worst.
 *  · **The two things read here are hints, not grants.** The `nonce` comparison binds a token response
 *    to the authorization request that asked for it, and `exp` is a fallback for computing expiry when
 *    `expires_in` is missing. Both make the app behave better; neither decides whether anything is
 *    allowed.
 *  · **Nothing decoded here is ever displayed as a verified fact.** In particular there is no
 *    `getEmail()` or `getSupplierId()` in this module, deliberately: `POST /submissions` refuses to
 *    accept technician identity from the client at all (C-12, T-12), and a UI that showed a name out of
 *    an unverified id_token would be presenting exactly the claim the server refuses to trust.
 *
 * ⚠️ **WHAT IS THEREFORE MISSING: signature verification.** With no signature check, the `nonce`
 * comparison in `oidc.ts` proves only that whoever answered the token POST knew the nonce. Over TLS to
 * the pool's own token endpoint that is a real constraint; it is not the RFC's full guarantee. If a
 * client-side check is ever wanted, `POOL.jwksUri` is recorded and `crypto.subtle.verify` with
 * `RSASSA-PKCS1-v1_5` / `SHA-256` (the pool signs `RS256`) is the shape — plus key caching and `kid`
 * selection. It is not built, and this comment exists so nobody concludes from the presence of this
 * file that it is.
 */

import { base64UrlDecodeToString } from './pkce';

export type JwtClaims = Readonly<Record<string, unknown>>;

/** Claims, or `null` for anything that is not a three-segment JWT with a JSON object payload. */
export function decodeJwtClaims(token: string | null | undefined): JwtClaims | null {
  if (typeof token !== 'string') {
    return null;
  }
  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }
  const payload = segments[1];
  if (payload === undefined || payload.length === 0) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(base64UrlDecodeToString(payload));
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    return parsed as JwtClaims;
  } catch {
    return null;
  }
}

/** A string claim, or `null`. Never coerces — a numeric `nonce` is not a nonce we sent. */
export function stringClaim(claims: JwtClaims | null, name: string): string | null {
  const value = claims?.[name];
  return typeof value === 'string' ? value : null;
}

/** `exp` as epoch **milliseconds**, or `null`. JWT `exp` is in seconds; forgetting that is a 1000× bug. */
export function expiryMsFromClaims(claims: JwtClaims | null): number | null {
  const exp = claims?.['exp'];
  if (typeof exp !== 'number' || !Number.isFinite(exp) || exp <= 0) {
    return null;
  }
  return Math.floor(exp * 1000);
}
